import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, FlaskConical, Database, ChevronRight, ArrowRight, AlertTriangle } from "lucide-react";
import { listProfiles, getLatest, latestForRig } from "@/lib/profiles/storage";
import { PROFILE_KIND_LABELS } from "@/lib/profiles/schemas";
import { newSession, attachProfile, saveDraft } from "@/lib/killsheet/sessionStore";
import { listRceCandidates, isRceCandidateId, materializeRceCandidate } from "@/lib/profiles/rceBridge";

// CreateKillSheetFromOpStateModal — single-tap kill-sheet bootstrap (P2).
//
// Goal: reduce the typical pre-kill workflow from ~12 manual taps to one.
//
// How the bindings are inferred:
//   1. Well + Mud  → from the OpState's `_importedFrom` audit trail
//      (these are the canonical 2 sources the OpState pull-drawer offers).
//   2. BHA + Casing + Pump → "latest" profile of each kind (fast default;
//      user can swap on the review screen).
//
// Audit trace on the resulting kill sheet:
//   session._originOpState = { id, version, label, at }
//
// Workflow:
//   1. Read OpState `_importedFrom` map → pre-select Well + Mud.
//   2. List latest of each kind → pre-select BHA, Casing, Pump.
//   3. Render review screen with variant picker + 5 swap dropdowns.
//   4. Allow Cancel.
//   5. On Create:
//        - newSession(variant)
//        - attach each selected profile via attachProfile()
//        - tag _originOpState
//        - saveDraft + navigate to /kill-sheet/<variant>
//
// data-testid contract:
//   create-killsheet-modal, -close, -cancel, -create, -variant,
//   -select-{kind}, -origin-pill, -warn-missing-{kind}

const VARIANTS = [
  { id: "surface-vertical",   label: "Surface · Vertical"   },
  { id: "surface-horizontal", label: "Surface · Horizontal" },
  { id: "subsea-vertical",    label: "Subsea · Vertical"    },
  { id: "subsea-horizontal",  label: "Subsea · Horizontal"  },
];

const BIND_KINDS = ["well", "bha", "casing", "mud", "pump"];

export default function CreateKillSheetFromOpStateModal({
  open,
  opstateRow,         // { id, version, label, payload }
  onClose,
}) {
  const nav = useNavigate();
  const [variant, setVariant] = useState("surface-vertical");
  const [selected, setSelected] = useState({});
  const [bindingError, setBindingError] = useState(null);

  // For each kind: pre-select the audited source (if any) else the latest.
  // Live Engineering Setup candidates (see lib/profiles/rceBridge.js) come
  // first so a fresh account with no legacy profiles still gets a sane
  // default selection instead of "No Profile found".
  const candidates = useMemo(() => {
    const out = {};
    BIND_KINDS.forEach((k) => { out[k] = [...listRceCandidates(k), ...listProfiles(k)]; });
    return out;
  }, [opstateRow?.id]);

  const auditedSources = useMemo(() => {
    const audit = opstateRow?.payload?._importedFrom || {};
    const m = {};
    Object.values(audit).forEach((row) => {
      // _importedFrom values look like { kind, id, version, field, at }
      if (row && row.kind && row.id && !m[row.kind]) m[row.kind] = row.id;
    });
    return m;
  }, [opstateRow]);

  useEffect(() => {
    if (!open) return;
    const opsRig = opstateRow?.payload?.rig || "";
    const init = {};
    BIND_KINDS.forEach((k) => {
      // Highest priority: audit-trail source if it still exists.
      const audited = auditedSources[k];
      if (audited && candidates[k]?.some((r) => r.id === audited)) {
        init[k] = audited;
        return;
      }
      // P3 v6 — prefer a profile tagged for the same rig before falling
      // back to the absolute latest.
      const rigHit = opsRig ? latestForRig(k, opsRig) : null;
      if (rigHit) {
        init[k] = rigHit.id;
        return;
      }
      init[k] = candidates[k]?.[0]?.id || "";
    });
    setSelected(init);
    setBindingError(null);
    setVariant("surface-vertical");
  }, [open, opstateRow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Presentation-only correction: lock background/page scroll while the
  // modal is open (the modal's own content stays scrollable via its own
  // overflow-y-auto), restoring the prior value on close/unmount. No Kill
  // Sheet data, formulas, storage, routing or schema logic touched.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  if (!open || !opstateRow) return null;

  const sourceOf = (kind) => {
    if (isRceCandidateId(selected[kind])) return "rce";
    if (auditedSources[kind] === selected[kind]) return "audit";
    const opsRig = opstateRow?.payload?.rig || "";
    if (opsRig) {
      const rigHit = latestForRig(kind, opsRig);
      if (rigHit && rigHit.id === selected[kind]) return "rig";
    }
    return "latest";
  };

  const handleCreate = () => {
    setBindingError(null);
    try {
      let session = newSession(variant);
      // Bind whichever profiles actually exist for this account. A kind with
      // no candidates is left unbound (session.<kind> stays null, as
      // newSession() already initializes it) rather than blocking creation
      // outright — the Kill Sheet editor already supports creating a
      // profile inline for any unbound kind via its own "+" control
      // (INLINE_PROFILE_CREATE_ENABLED in KillSheetEditor.jsx), so there is
      // nothing here that actually needs to gate the one-tap flow.
      BIND_KINDS.forEach((k) => {
        if (!selected[k]) return;
        // An Engineering-Setup-sourced pick isn't a real legacy profile yet --
        // materialize it first (createProfile under the hood, see
        // rceBridge.js) so it snapshots exactly like a manually-picked
        // profile from here on.
        const profileId = isRceCandidateId(selected[k])
          ? materializeRceCandidate(k, selected[k]).id
          : selected[k];
        session = attachProfile(JSON.parse(JSON.stringify(session)), k, profileId);
      });
      // Also bind the originating OpState so its dynamic fields flow into the
      // kill sheet via the editor's shadow-merge.
      session = attachProfile(JSON.parse(JSON.stringify(session)), "opstate", opstateRow.id);
      // Audit: where did this kill sheet come from?
      session._originOpState = {
        id:      opstateRow.id,
        version: opstateRow.version,
        label:   opstateRow.label || opstateRow.payload?.label || "(unnamed)",
        at:      new Date().toISOString(),
      };
      saveDraft(session);
      onClose && onClose();
      nav(`/kill-sheet/${variant}`);
    } catch (e) {
      setBindingError(e?.message || "Unable to create kill sheet from this OpState.");
    }
  };

  // Presentation-only correction: rendered via a portal to document.body so
  // it escapes Home's (or any host page's) stacking context, and raised to
  // z-[10050] - above the bottom navigation's z-index:10000 (Layout.jsx) -
  // so the nav can no longer paint over or stay clickable through the
  // modal. No Kill Sheet data, formulas, storage, routing or schema logic
  // changed below.
  return createPortal(
    <div data-testid="create-killsheet-modal"
         className="fixed inset-0 z-[10050] flex items-center justify-center"
         style={{ background: "rgba(0,0,0,0.6)" }}
         role="dialog" aria-modal="true">
      <div className="card-rs max-w-2xl w-[min(680px,calc(100vw-32px))] mx-4 max-h-[92vh] overflow-y-auto"
           style={{ background: "var(--rs-bg-1, #0A0B0E)" }}>
        <header className="sticky top-0 z-10 flex items-center justify-between px-4 py-3"
                style={{ background: "rgba(10,11,14,0.96)", borderBottom: "1px solid var(--rs-border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)]">
              One-tap workflow
            </div>
            <h3 className="text-[15px] font-semibold text-[var(--rs-text-1)]">
              Create Kill Sheet from this Operational State
            </h3>
          </div>
          <button data-testid="create-killsheet-close" type="button" onClick={onClose}
                  className="p-1.5 rounded hover:text-[var(--rs-amber)] text-[var(--rs-text-2)]"
                  title="Close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-3 space-y-4">
          <div data-testid="create-killsheet-origin-pill"
               className="card-rs p-2 text-[11px]"
               style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.06)" }}>
            <div className="text-[var(--rs-amber)] inline-flex items-center gap-1 font-mono uppercase tracking-[0.18em]">
              <FlaskConical className="w-3 h-3" /> Origin OpState
            </div>
            <div className="text-[var(--rs-text-1)] mt-1">
              <strong>{opstateRow.label || opstateRow.payload?.label || "(unnamed)"}</strong>
              <span className="text-[var(--rs-text-3)]"> · v{opstateRow.version}</span>
            </div>
            <p className="text-[10.5px] text-[var(--rs-text-3)] leading-snug mt-1">
              Each bound profile is tagged with its provenance. Profiles already
              referenced by this OpState's import audit are pre-selected;
              others default to the most recent profile of that kind. You
              can swap any of them before creating the kill sheet.
            </p>
          </div>

          {/* Variant */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-1.5">
              Kill sheet variant
            </div>
            <select data-testid="create-killsheet-variant"
                    value={variant}
                    onChange={(e) => setVariant(e.target.value)}
                    className="input-rs text-[12px] w-full" style={{ height: 38 }}>
              {VARIANTS.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </section>

          {/* Bindings preview */}
          <section data-testid="create-killsheet-bindings">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-1.5">
              Profile bindings (5)
            </div>
            <div className="flex flex-col gap-2">
              {BIND_KINDS.map((k) => {
                const rows = candidates[k] || [];
                const selectedRow = rows.find((r) => r.id === selected[k]);
                const src = sourceOf(k);
                const hasRows = rows.length > 0;
                return (
                  <div key={k} className="card-rs p-2 grid grid-cols-12 items-center gap-2"
                       data-testid={`create-killsheet-row-${k}`}
                       style={{
                         background: hasRows ? "rgba(22,24,29,0.4)" : "rgba(252,165,165,0.06)",
                         borderColor: hasRows ? undefined : "rgba(252,165,165,0.4)",
                       }}>
                    <div className="col-span-3 text-[11px] uppercase font-mono text-[var(--rs-text-3)]">
                      {k}
                    </div>
                    {hasRows ? (
                      <>
                        <select data-testid={`create-killsheet-select-${k}`}
                                value={selected[k] || ""}
                                onChange={(e) => setSelected({ ...selected, [k]: e.target.value })}
                                className="input-rs col-span-7 text-[12px]"
                                style={{ minHeight: 32 }}>
                          {rows.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.label} · v{r.version}
                            </option>
                          ))}
                        </select>
                        <div className="col-span-2 flex justify-end">
                          <span data-testid={`create-killsheet-source-${k}`}
                                className="text-[9.5px] px-2 py-0.5 rounded-md inline-flex items-center gap-1 whitespace-nowrap"
                                style={
                                  src === "rce"
                                    ? { background: "rgba(56,189,248,0.10)", color: "#38BDF8", border: "1px solid rgba(56,189,248,0.35)" }
                                    : src === "audit"
                                    ? { background: "rgba(245,180,0,0.10)", color: "var(--rs-amber)", border: "1px solid rgba(245,180,0,0.35)" }
                                    : src === "rig"
                                    ? { background: "rgba(34,197,94,0.10)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.35)" }
                                    : { background: "rgba(148,163,184,0.10)", color: "var(--rs-text-3)", border: "1px solid var(--rs-border)" }
                                }>
                            <Database className="w-2.5 h-2.5" />
                            {src === "rce" ? "Engineering Setup"
                              : src === "audit" ? "from OpState audit"
                              : src === "rig" ? `rig: ${opstateRow?.payload?.rig || ""}`
                              : "latest"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div data-testid={`create-killsheet-warn-missing-${k}`}
                           className="col-span-9 text-[11px] text-[#FCA5A5] inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        No {PROFILE_KIND_LABELS[k]?.replace(/ Profiles$/, " Profile") || k} found yet — you can create one on the Kill Sheet after this step.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {bindingError && (
            <div data-testid="create-killsheet-error"
                 className="card-rs p-2 text-[11px]"
                 style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
              <div className="font-semibold text-[#FCA5A5] uppercase tracking-wider mb-1">
                Cannot create kill sheet
              </div>
              <div className="text-[var(--rs-text-1)]">{bindingError}</div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
                    data-testid="create-killsheet-cancel"
                    className="px-4 py-2 rounded-md text-[13px]"
                    style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
              Cancel
            </button>
            <button type="button" onClick={handleCreate}
                    data-testid="create-killsheet-create"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                    style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
              Create Kill Sheet <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Convenience: fetch the OpState row for the modal in one call.
export function loadOpStateRow(id) {
  return getLatest("opstate", id);
}
